import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MemberList } from "@/features/chat/components/MemberList";
import { SelectedRecipients } from "@/features/chat/components/SelectedRecipients";
import { StudyGroupSelector } from "@/features/chat/components/StudyGroupSelector";
import type { RecipientSelectorDialogProps } from "@/features/chat/types";
import type { UserShort } from "@/lib/humhub-api/types";

export const RecipientSelectorDialog = ({
  open,
  onClose,
  onRecipientsSelected,
  initialRecipients = [],
}: RecipientSelectorDialogProps) => {
  const { t } = useTranslation();
  const [selectedRecipients, setSelectedRecipients] = useState<UserShort[]>([...initialRecipients]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");

  useEffect(() => {
    if (open) {
      setSelectedRecipients([...initialRecipients]);
      setSelectedGroupId("");
    }
  }, [open, initialRecipients]);

  const handleMemberSelect = (member: UserShort) => {
    if (!selectedRecipients.some((r) => r.guid === member.guid)) {
      setSelectedRecipients([...selectedRecipients, member]);
    }
  };

  const handleRecipientRemove = (recipientToRemove: UserShort) => {
    setSelectedRecipients(selectedRecipients.filter((r) => r.guid !== recipientToRemove.guid));
  };

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroupId(groupId);
  };

  const handleConfirm = () => {
    onRecipientsSelected(selectedRecipients);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden sm:max-w-md">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t("chat.selectRecipients")}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          <div className="space-y-4">
            {selectedRecipients.length > 0 && (
              <SelectedRecipients
                recipients={selectedRecipients}
                onRemove={handleRecipientRemove}
              />
            )}

            <StudyGroupSelector
              selectedGroupId={selectedGroupId}
              onGroupSelect={handleGroupSelect}
            />

            {selectedGroupId && (
              <MemberList
                selectedGroupId={selectedGroupId}
                selectedRecipients={selectedRecipients}
                onMemberSelect={handleMemberSelect}
              />
            )}
          </div>
        </div>

        <DialogFooter className="mt-4 flex-shrink-0 sm:justify-between">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="teal" onClick={handleConfirm} disabled={selectedRecipients.length === 0}>
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
